<template>
  <b-card class="reward-tokens-history-card" no-body>
    <b-card-header class="py-3 d-flex justify-content-between align-items-center">
      <h4 class="mb-0"><strong>{{ $t('reward-token-history.title') }}</strong></h4>
    </b-card-header>
    <b-card-body>
      <div v-if="!tokensHistoryItems || tokensHistoryItems.length === 0" class="py-4 d-flex justify-content-center">
        <span class="text-muted">{{ $t('reward-token-history.no-transactions') }}</span>
      </div>
      <div v-else
        v-for="{ id, tokenCost, text, date } in tokensHistoryItems"
        class="d-flex w-100 justify-content-between border-bottom py-3"
        :key="id"
      >
        <div class="d-flex justify-content-start">
          <div style="width: 86px">
            <span :class="{'balance-badge': true, red: tokenCost < 0, green: tokenCost > 0 }">
              {{ tokenCost > 0 ? '+' : '-' }}{{ Math.abs(tokenCost) }}
            </span>
          </div>
          <div style="max-width: 360px"><div v-html="text"></div></div>
        </div>
        <div style="width: 70px; text-align: right"><small>{{ date }}</small></div>
      </div>
      <div class="d-flex flex-column justify-content-center align-items-center">
        <b-pagination
          class="mb-0 mt-3"
          v-if="showAllItems && (rewardTokensHistory || []).length > perPage"
          v-model="currentPage"
          :total-rows="(rewardTokensHistory || []).length"
          :per-page="perPage"
          size="sm"
        ></b-pagination>
        <b-link
          class="mt-3"
          v-if="(rewardTokensHistory || []).length > 1"
          @click="showAllItems = !showAllItems"
        >{{ showAllItems ? $t('rewards.show-less') : $t('rewards.show-more') }}</b-link>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import distanceInWords from 'date-fns/distance_in_words';

export default {
  props: {
    rewardTokensHistory: {
      type: Array,
      default: () => null,
    },
    rewardTokensBalance: {
      type: Number,
      default: () => null,
    },
  },
  data() {
    return {
      perPage: 5,
      currentPage: 1,
      showAllItems: false,
    };
  },
  computed: {
    tokensHistoryItems() {
      const { rewardTokensHistory, perPage, currentPage, showAllItems } = this;
      return (Array.isArray(rewardTokensHistory) ? rewardTokensHistory : []).map(({ id, eventName, tokenCost, job, application, contentShare, claimedReward, createdAt }) => {
        const { fullName, job: applicationJob } = application || {};
        const { rewardData, reward } = claimedReward || {};
        const { title } = rewardData || applicationJob || job || reward || contentShare || {};
        const result = {
          id,
          tokenCost,
          date: this.timeAgo(createdAt),
        };
        switch (eventName) {
          case 'CONTENT_SCHEDULE_SHARE_FIRST':
          case 'CONTENT_SHARED':
            return {
              ...result,
              text: title ? this.$t('reward-token-history.shared-content', { title }) : this.$t('reward-token-history.shared-content-unknown'),
            };
          case 'JOB_SCHEDULE_SHARE_FIRST':
          case 'JOB_SHARED':
          case 'REFERRAL_SUGGESTED':
            return {
              ...result,
              text: title ? this.$t('reward-token-history.shared-job', { title }) : this.$t('reward-token-history.shared-job-unknown'),
            };
          case 'SHARE_VISITED':
          case 'SHARE_UNIQUE_VISITED':
          case 'SHARE_FIRST_VISITED':
            return {
              ...result,
              text: title ? this.$t('reward-token-history.visited-job', { title }) : this.$t('reward-token-history.visited-job-unknown'),
            };
          case 'CONTENT_SHARE_VISITED':
          case 'CONTENT_SHARE_UNIQUE_VISITED':
          case 'CONTENT_SHARE_FIRST_VISITED':
            return {
              ...result,
              text: title ? this.$t('reward-token-history.visited-content', { title }) : this.$t('reward-token-history.visited-content-unknown'),
            };
          case 'APPLICATION_CREATED':
          case 'APPLICATION_CONFIRMED':
            return {
              ...result,
              text: this.$t('reward-token-history.application-created', {
                candidate: fullName || this.$t('reward-token-history.unknown-candidate'),
                job: title || this.$t('reward-token-history.unknown-job'),
              }),
            };
          case 'APPLICATION_INTERVIEW':
            return {
              ...result,
              text: this.$t('reward-token-history.interviewed', {
                candidate: fullName || this.$t('reward-token-history.unknown-candidate'),
                job: title || this.$t('reward-token-history.unknown-job'),
              }),
            };
          case 'APPLICATION_HIRED':
            return {
              ...result,
              text: this.$t('reward-token-history.hired', {
                candidate: fullName || this.$t('reward-token-history.unknown-candidate'),
                job: title || this.$t('reward-token-history.unknown-job'),
              }),
            };
          case 'CLAIM_REQUESTED':
            return {
              ...result,
              text: this.$t('reward-token-history.reward-claim-created', { title: title ? title.trim() : this.$t('reward-token-history.unknown-reward') }),
            };
          case 'CLAIM_REJECTED':
            return {
              ...result,
              text: this.$t('reward-token-history.reward-claim-rejected', { title: title ? title.trim() : this.$t('reward-token-history.unknown-reward') }),
            };
          case 'RAFFLE_LEADERBOARD':
            return {
              ...result,
              text: this.$t('reward-token-history.leaderboard-results'),
            };
          case 'REMOVED_TOKENS':
            return {
              ...result,
              text: this.$t('reward-token-history.removed-by-administrator'),
            };
          case 'CELEBRATE_SUCCESS':
            return {
              ...result,
              text: this.$t('reward-token-history.celebrating-success'),
            };
          case 'ADDED_TOKENS':
          case 'MANUAL_APPLICATION':
          case 'MANUAL_INTERVIEW':
          case 'MANUAL_HIRE':
          case 'MANUAL_ADHOC':
            return {
              ...result,
              text: this.$t('reward-token-history.added-by-administrator'),
            };
          case 'ASSURANT_BACKFILL':
            return {
              ...result,
              text: this.$t('reward-token-history.assurant-backfill'),
            };
          default:
            return {
              ...result,
              text: eventName.trim(),
            };
        }
      }).slice(showAllItems ? (currentPage - 1) * perPage : 0, showAllItems ? currentPage * perPage : 1);
    },
  },
  methods: {
    timeAgo(createdAt) {
      return distanceInWords(new Date(), createdAt, { addSuffix: true })
        .replace('about ', '')
        .replace('less than a minute ago', '1m ago')
        .replace(' seconds', 's')
        .replace(' second', 's')
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' weeks', 's')
        .replace(' week', 's')
        .replace(' months', 'mo')
        .replace(' month', 'mo')
        .replace(' years', 'y')
        .replace(' year', 'y');
    },
  },
};
</script>

<style lang="scss">
.reward-tokens-history-card {
  .balance-badge {
    padding: 4px 8px;
    background-color: #ebf2ff;
    border-radius: 3px;
    font-weight: bold;

    &.red {
      background-color: #ffebeb;
    }
    &.green {
      background-color: #edffeb;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/rewards/RewardTokensHistory.vue